import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { useBalticStore } from '@/store/balticStore';
import { Button } from '@/components/ui';
import type { BalticHistoryRequest, BalticRoute } from '@/types/baltic.types';
import { DragHandle, type DragHandleProps } from './DraggableWidget';
import {
  formatBalticValue,
  formatBalticChange,
  getChangeColorClass,
  hasTIAssessment,
} from '@/utils/balticUtils';

function Sparkline({ route }: { route: BalticRoute }) {
  const change = route.biValueChange || 0;
  const points = 8;
  const width = 40;
  const height = 16;

  const trendPath = Array.from({ length: points })
    .map((_, i) => {
      const x = (i / (points - 1)) * width;
      const trend = change > 0 ? 1 : change < 0 ? -1 : 0;
      const y = height / 2 + (Math.random() - 0.5 + trend * 0.3) * (height / 3);
      return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
    })
    .join(' ');

  const strokeColor = change > 0 ? '#10b981' : change < 0 ? '#ef4444' : '#6b7280';

  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <path d={trendPath} fill="none" stroke={strokeColor} strokeWidth="1.25" />
    </svg>
  );
}

export function BalticWidget({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) {
  const { routes, selectedPeriod, loading, fetchRoutes, setSelectedPeriod } = useBalticStore();
  const [hasRequestedInitialData, setHasRequestedInitialData] = useState(false);

  // Fetch routes on mount
  useEffect(() => {
    setHasRequestedInitialData(true);
    fetchRoutes();
  }, [fetchRoutes]);

  const showLoadingState = loading || (!hasRequestedInitialData && routes.length === 0);

  const handleRefresh = () => {
    fetchRoutes();
  };

  const handlePeriodChange = (period: BalticHistoryRequest['period']) => {
    setSelectedPeriod(period);
  };

  const periodOptions: Array<{ label: string; value: BalticHistoryRequest['period'] }> = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Yearly', value: 'yearly' },
  ];

  return (
    <div className="flex h-full flex-col">
      {/* Header — aligned with Fixed Days widget */}
      <div className="-mx-px -mt-px mb-2 flex items-center justify-between rounded-t-lg bg-gray-200/60 px-4 py-2 dark:bg-dark-700 sm:mb-2.5 sm:px-5 sm:py-2.5">
        <div className="min-w-0 pr-2">
          <h2
            className="truncate text-base font-semibold text-gray-900 dark:text-white sm:text-lg"
            title="Baltic Exchange indices"
          >
            Baltic
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {dragHandleProps && <DragHandle {...dragHandleProps} />}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Link to="/baltic">
            <Button variant="ghost" size="sm" title="View all Baltic data">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Period selector */}
      <div className="mb-2 flex gap-1 px-4 sm:mb-2.5 sm:px-5">
        {periodOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handlePeriodChange(option.value)}
            className={`flex-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium transition-colors sm:px-2 sm:py-1 sm:text-xs ${
              selectedPeriod === option.value
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-300 dark:hover:bg-dark-600'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Routes list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 sm:px-5 sm:pb-5">
        {showLoadingState && routes.length === 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-dark-700">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="flex items-center justify-between py-2">
                <div className="flex-1">
                  <div className="mb-1.5 h-4 w-20 animate-pulse rounded bg-gray-200 dark:bg-dark-700" />
                  <div className="h-3 w-32 animate-pulse rounded bg-gray-100 dark:bg-dark-700" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-8 w-12 animate-pulse rounded bg-gray-200 dark:bg-dark-700" />
                  <div className="h-8 w-12 animate-pulse rounded bg-gray-200 dark:bg-dark-700" />
                  <div className="h-5 w-12 animate-pulse rounded bg-gray-100 dark:bg-dark-700" />
                </div>
              </div>
            ))}
          </div>
        ) : routes.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-dark-700">
            {routes.slice(0, 6).map((route) => (
              <Link
                key={route.route}
                to={`/baltic?route=${encodeURIComponent(route.route)}&period=${selectedPeriod}`}
                className="flex items-center justify-between gap-2 py-2 transition-colors hover:bg-gray-50 dark:hover:bg-dark-700"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {route.route}
                  </div>
                  <div className="truncate text-[11px] text-gray-500 dark:text-gray-400 sm:text-xs">
                    {route.description}
                  </div>
                </div>
                <div className="ml-1 flex shrink-0 items-center gap-3 text-right tabular-nums sm:gap-4">
                  <div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 sm:text-xs">
                      BITR
                    </div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatBalticValue(route.biValue, route.decimals)}
                    </div>
                    <div
                      className={`text-[11px] sm:text-xs ${getChangeColorClass(route.biValueChange)}`}
                    >
                      {formatBalticChange(route.biValueChange, route.decimals)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 sm:text-xs">
                      TI
                    </div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {hasTIAssessment(route)
                        ? formatBalticValue(route.tiValue, route.decimals)
                        : 'N/A'}
                    </div>
                    {hasTIAssessment(route) && (
                      <div
                        className={`text-[11px] sm:text-xs ${getChangeColorClass(route.tiValueChange)}`}
                      >
                        {formatBalticChange(route.tiValueChange, route.decimals)}
                      </div>
                    )}
                  </div>
                  <Sparkline route={route} />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex min-h-[8rem] items-center justify-center px-2 text-center text-xs text-gray-500 dark:text-gray-400 sm:min-h-[10rem] sm:text-sm">
            No Baltic routes available
          </div>
        )}
      </div>
    </div>
  );
}
